import moment from 'moment';
import api from '@/services/Api';
import { ActionContext, Module } from 'vuex';
import Arbeitsplatz from '@/types/Arbeitsplatz';
import { successSwal, warningSwal } from '@/helper/swals';
import { GlobalsState, PrintData, PrintPayload, RootState } from '@/store/types';
import Titel from '@/types/Titel';
import { Geschlecht } from '@/types/Geschlecht';
import jspm from '@/services/PrintManager';
import { InputValidationRules } from 'vuetify';
import { SystemEinstellung, SystemEinstellungKey } from '@/types/Einstellung';
import _ from 'lodash';
import { saveAs } from 'file-saver';

type Context = ActionContext<GlobalsState, RootState>;

export const globals: Module<GlobalsState, RootState> = {
    namespaced: true,
    state: {
        rules: {
            required: (value: unknown): boolean | string => {
                return (value !== null &&
                    value !== undefined &&
                    (typeof value === 'string' ? value.trim() !== '' : true) &&
                    (Array.isArray(value) ? value.length > 0 : true)) || 'Erforderlich.';
            },
            maxLength: (length: number) => {
                return (value: string | null): boolean | string => {
                    return ((value?.toString()?.length || 0) <= length) || `Maximal ${length} Buchstaben erlaubt.`;
                };
            },
            maxNumberLength: (length: number) => {
                return (value: string | null): boolean | string => {
                    return ((value?.toString()?.length || 0) <= length) || `Maximal ${length} Ziffern erlaubt.`;
                };
            },
            onlyNumbers: (value: string | null): boolean | string => {
                return /^(\d{0,})$/.test(value?.toString() || '0') || 'Es dürfen nur Zahlen eingeben werden.';
            },
            onlyNumbersAllowNegative: (value: string | null): boolean | string => {
                return /^(-?\d{0,})$/.test(value?.toString() || '0') || 'Es dürfen nur Zahlen eingeben werden.';
            },
            numeric: (precision: number, scale: number) => {
                return (value: string | null): boolean | string => {
                    value = (value?.toString() ?? '0').replaceAll('.', '');
                    return new RegExp(`^[-]?[0-9]{0,${precision - scale}}[,]?[0-9]{0,${scale}}$`).test(value) ||
                        `Wert muss numerisch sein z.B. 1,${'9'.padStart(scale, '9')}`;
                };
            },
            isNumber: (value: string | null): boolean | string => {
                return /^(\d{0}|[0-9]{4})$/.test(value?.toString() || '0') ||
                    'Es müssen vier Zahlen eingegeben werden.';
            },
            minLength: (length: number) => {
                return (value: string | null): boolean | string => {
                    return ((value?.toString()?.length || 0) >= length) || `Minimum ${length} Buchstaben erforderlich.`;
                };
            },
            minNumberLength: (length: number) => {
                return (value: string | null): boolean | string => {
                    return ((value?.toString()?.length || 0) >= length) || `Minimum ${length} Zahlen erforderlich.`;
                };
            },
            exactDigits: (length: number) => {
                return (value: string | null): boolean | string => {
                    return ((value?.toString()?.length || 0) >= length) || `Es sind ${length} Ziffern erforderlich.`;
                };
            },
            validGender: (genders: Geschlecht[]) => {
                return (value: string | null): boolean | string => {
                    return (genders.find((geschlecht) => geschlecht === value) !== undefined) || 'Erforderlich.';
                };
            },
            validSvnr: (value: string | null): boolean | string => {
                if (value?.length === 10) {
                    const svnr = value.split('').map((zahl: string) => parseInt(zahl, 10));

                    const rest = [[0, 3], [1, 7], [2, 9], [4, 5], [5, 8], [6, 4], [7, 2], [8, 1], [9, 6]]
                        .map(([index, multiplier]: number[]) => svnr[index] * multiplier)
                        .reduce((acc: number, val: number) => acc += val, 0) % 11;

                    if (rest === svnr[3]) {
                        return true;
                    }
                }

                return 'Ungültige Sozialversicherungsnummer.';
            },
/*            validTelefonnummer: (value: string): boolean | string => {
                if(value) {
                    return /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im.test(value) ||
                        'Ungültige Telefonnummer';
                } else return null;
            },*/
            validPrice: (value: string): boolean | string => {
                return /^(\d{0}| | \d+(?:[.,]\d{1,3})?)$/.test(value) ||
                    'Preise müssen nach dem Komma mindestens eine Stelle besitzen.';
            },
            validDate: (date: string | null): boolean | string => {
                return moment(date, ['YYYY-MM-DD'], true).isValid() || 'Datum entspricht nicht dem erlaubten Format.';
            },
            validTime: (time: string | null): boolean | string => {
                return moment(time, ['HH:mm'], true).isValid() || 'Zeit entspricht nicht dem erlaubten Format.';
            },
            fromValidator(validator: Record<string, unknown>): InputValidationRules {
                return Object.entries(validator ?? {})
                    .map(([validationType, validationValue]) => {
                        switch (validationType) {
                            case 'maxlen':
                                return this.maxLength(validationValue as number);
                            case 'required':
                                return this.required;
                            default:
                                return null;
                        }
                    })
                    .filter((rule) => rule !== null) as InputValidationRules;
            },
        },
        titelauswahl: [],
        printers: {
            druckhinweis: false,
            arbeitsplatzhinweis: false,
            arbeitsplatzhinweisSource: '',
            data: null,
            status: '',
        },
        formatDate(input: string | null): string | null {
            if (!input) {
                return '';
            }

            const date = moment(input.split(' ')[0], ['YYYY-MM-DD'], true);
            return date.isValid() ? date.format('DD.MM.YYYY') : input;
        },
        parseDate(input: string | null): string | null {
            if (!input) {
                return '';
            }

            const date = moment(input.split(' ')[0], ['DD.MM.YYYY'], true);
            return date.isValid() ? date.format('YYYY-MM-DD') : input;
        },
        formatTime(input: string | null): string | null {
            if (!input) {
                return '';
            }

            const date = moment(input.slice(0, 16), ['YYYY-MM-DD HH:mm'], true);
            return date.isValid() ? date.format('HH:mm') : input;
        },
        parseTime(input: string | null): string | null {
            if (!input) {
                return '';
            }

            const date = moment(input.slice(0, 16), ['DD.MM.YYYY HH:MM'], true);
            return date.isValid() ? date.format('HH:mm:ss') : input;
        },
        sortDate<T>(a: T, b: T): number {
            if (a === b) {
                return 0;
            } else if (!a) {
                return 1;
            } else if (!b) {
                return -1;
            } else {
                const d1 = moment(b, ['DD.MM.YYYY']).unix();
                const d2 = moment(a, ['DD.MM.YYYY']).unix();
                return d1 === d2 ? 0 : d1 < d2 ? 1 : -1;
            }
        },
    },
    actions: {
        loadTitle({ commit }: Context): void {
            api.get('verwaltung/titel/load')
                .then((response) => commit('setTitle', response.data.body))
                .catch(console.error);
        },
        print({ dispatch, rootGetters }: Context, payload: PrintPayload & { printerName: string, checkTrays?: boolean }): void {
            const { printerName, data, count, printRotation, paperName, duplex, checkTrays } = payload;

            if (!data) {
                console.warn('print(global): data in PrintPayload not set');
                return;
            }

            if (process.env.VUE_APP_DEBUG_PRINT_SAVEAS === '1') {
                saveAs(data, `Test_${moment().format('YYYY-MM-DD_HHmmss')}.pdf`);
                return;
            }

            if (!jspm.printer?.hasConnected()) {
                dispatch('setDialogDruckhinweis', true);
                return;
            }

            const {
                JSPrintManager,
                ClientPrintJob,
                PrintFilePDF,
                FileSourceType,
                InstalledPrinter,
                PrintRotation,
                WSStatus,
                DuplexMode,
            } = jspm.printer.manager;

            if (JSPrintManager.websocket_status !== WSStatus.Open) {
                dispatch('setDialogDruckhinweis', true);
                return;
            }

            const getSystemEinstellung: <T>(parameter: SystemEinstellungKey) => SystemEinstellung<T> =
                rootGetters['authentication/getSystemEinstellung'];

            const blob = data instanceof Blob
                ? data
                : new Blob([data], { type: 'application/pdf' });

            blob.slice(0, 5).text().then((signature: string) => {
                if (signature !== '%PDF-') {
                    warningSwal('Drucker', 'Die Druckdaten sind kein gueltiges PDF.');
                    console.error('print(global): invalid PDF payload signature', signature);
                    return;
                }

                const copies = Math.max(1, Math.min(10, count ?? 1));
                const pdf = new PrintFilePDF(blob, FileSourceType.BLOB, 'druck.pdf', copies);
                pdf.printRotation = printRotation ?? PrintRotation.None;

                const printer = new InstalledPrinter(printerName);
                printer.paperName = paperName ?? '';
                printer.duplex = duplex ?? DuplexMode.Default;

                if (getSystemEinstellung('muw').isEnabled() && paperName === 'A5' && checkTrays === true) {
                    printer.trayName = 'Magazin 2';
                    printer.paperName = '';
                }

                if (String(getSystemEinstellung('druckvariante').value) === 'MUG' && checkTrays === true) {
                    printer.trayName = 'Kassette 1';
                } else if (String(getSystemEinstellung('druckvariante').value) === 'MUG' && checkTrays !== true) {
                    printer.trayName = 'Kassette 2';
                }

                const job = new ClientPrintJob();
                job.clientPrinter = printer;
                job.files.push(pdf);

                job.sendToClient()
                    .then(() => successSwal('JSPrintManager', 'Drucker beauftragt'))
                    .catch(console.error);
            }).catch((error: unknown) => {
                console.error('print(global): failed to read PDF signature', error);
                warningSwal('Drucker', 'Die Druckdaten konnten nicht gelesen werden.');
            });
        },
        printDokument({ commit, dispatch, rootGetters }: Context, payload: PrintPayload): void {
            const arbeitsplatz = rootGetters['authentication/arbeitsplatz'];

            if (arbeitsplatz.arbeitsplatz_id) {
                dispatch('print', { printerName: arbeitsplatz.standarddrucker, ...payload });
            } else {
                commit('setPrintData', { printTarget: 'standarddrucker', payload });
                commit('setDialogArbeitsplatzhinweis', true);
            }
        },
        printRezept({ commit, dispatch, rootGetters }: Context, payload: PrintPayload): void {
            const arbeitsplatz = rootGetters['authentication/arbeitsplatz'];

            const getSystemEinstellung: <T>(parameter: SystemEinstellungKey) => SystemEinstellung<T> =
                rootGetters['authentication/getSystemEinstellung'];

            let paperName = '';

         if (String(getSystemEinstellung('rezeptformular').value) === 'A5') {
                paperName = 'A5';
            } else paperName = 'A4';

         const checkTrays = getSystemEinstellung('muw').isEnabled();

            if (arbeitsplatz.arbeitsplatz_id) {
                dispatch('print', { printerName: arbeitsplatz.rezeptdrucker, paperName, checkTrays, ...payload });
            } else {
                commit('setPrintData', { printTarget: 'rezeptdrucker', payload });
                commit('setDialogArbeitsplatzhinweis', true);
            }
        },
        printEtiketten({ commit, dispatch, rootGetters }: Context, payload: PrintPayload): void {
            const arbeitsplatz = rootGetters['authentication/arbeitsplatz'];
            if (arbeitsplatz.arbeitsplatz_id) {
                dispatch('print', { printerName: arbeitsplatz.etikettendrucker, ...payload });
            } else {
                commit('setPrintData', { printTarget: 'etikettendrucker', payload });
                commit('setDialogArbeitsplatzhinweis', true);
            }
        },
        printImpfetiketten({ dispatch, rootGetters }: Context, payload: PrintPayload): void {
            const getSystemEinstellung: <T>(parameter: SystemEinstellungKey) => SystemEinstellung<T> =
                rootGetters['authentication/getSystemEinstellung'];
            const arbeitsplatz = rootGetters['authentication/arbeitsplatz'];
            dispatch('print', { printerName: getSystemEinstellung('impfetiketten_drucker').value ?? arbeitsplatz.standarddrucker, ...payload });
        },
        saveArbeitsplatzAndPrintOrGetCardInfo({ commit, dispatch, state }: Context, arbeitsplatz: Arbeitsplatz): void {
            const printData = state.printers.data;
            const source = state.printers.arbeitsplatzhinweisSource;

            commit('session/setSelectedArbeitsplatz', arbeitsplatz.arbeitsplatz_id, { root: true });

            if (printData) {
                let printerName = null;

                if (printData.printTarget === 'rezeptdrucker') {
                    printerName = arbeitsplatz[printData.printTarget] ?? arbeitsplatz.standarddrucker;
                } else {
                    printerName = arbeitsplatz[printData.printTarget];
                }

                dispatch('print', { printerName, ...printData.payload });
            } else if (source !== 'warteliste') {
                dispatch('patient/getCardInfo', null, { root: true });
            }

            commit('setDialogArbeitsplatzhinweis', false);
        },
        setDialogDruckhinweis({ commit }: Context, visible: boolean): void {
            commit('setDialogDruckhinweis', visible);
        },
        setPrinterStatus({ commit }: Context, status: string): void {
            commit('setPrinterStatus', status);
        },
    },
    mutations: {
        setTitle(state: GlobalsState, title: Titel[]): void {
            state.titelauswahl = title;
        },
        setDialogDruckhinweis(state: GlobalsState, visible: boolean): void {
            state.printers.druckhinweis = visible;
        },
        setDialogArbeitsplatzhinweis(state: GlobalsState, visible: boolean): void {
            state.printers.arbeitsplatzhinweis = visible;

            if (!visible) {
                state.printers.arbeitsplatzhinweisSource = '';
            }
        },
        setDialogArbeitsplatzhinweisSource(state: GlobalsState, source: string): void {
            state.printers.arbeitsplatzhinweisSource = source;
        },
        setPrintData(state: GlobalsState, data: PrintData | null): void {
            state.printers.data = data;
        },
        setPrinterStatus(state: GlobalsState, status: string): void {
            state.printers.status = status;
        },
    },
};
